/**
 * HTTP calls to the backend's auth proxy (`/auth/*` — see
 * `docs/api-schema.md` §Auth), which wraps Cognito. These are **public**
 * endpoints (no bearer) over plain HTTPS. Port of `app/src/auth/authApi.ts`
 * minus Sentry; the web app never imports from `app/src/auth/`.
 */

import { API_BASE_URL } from '@/api/config';

import type { AuthProviderKind, Session } from '@/auth/types';

/** Auth error carrying the HTTP status so callers can branch (401/403/409…). */
export class AuthApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'AuthApiError';
  }
}

export interface AuthTokens {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

export interface AuthChallenge {
  challenge: 'NEW_PASSWORD_REQUIRED';
  session: string;
}

export type SignInResponse = AuthTokens | AuthChallenge;

export interface RefreshResponse {
  access_token: string;
  id_token?: string;
  expires_in?: number;
}

async function post<T>(path: string, body: Record<string, string>): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (e) {
    // Network / DNS failure (status 0) — distinct from an HTTP 401.
    throw new AuthApiError(0, e instanceof Error ? e.message : 'Network error');
  }
  const json = (await res.json().catch(() => ({}))) as { error?: string; message?: string } & T;
  if (!res.ok) {
    const detail = json.error ?? json.message;
    throw new AuthApiError(res.status, detail ?? `Request failed (${res.status})`);
  }
  return json;
}

export const emailAuth = {
  signUp(email: string, password: string): Promise<{ confirmed: boolean; user_sub: string }> {
    return post('/auth/signup', { email, password });
  },
  confirm(email: string, code: string): Promise<{ confirmed: boolean }> {
    return post('/auth/confirm', { email, code });
  },
  resendCode(email: string): Promise<{ sent: boolean }> {
    return post('/auth/resend-code', { email });
  },
  signIn(email: string, password: string): Promise<SignInResponse> {
    return post<SignInResponse>('/auth/signin', { email, password });
  },
  refresh(refreshToken: string): Promise<RefreshResponse> {
    return post<RefreshResponse>('/auth/refresh', { refresh_token: refreshToken });
  },
  forgotPassword(email: string): Promise<{ sent: boolean }> {
    return post('/auth/forgot-password', { email });
  },
  confirmForgotPassword(email: string, code: string, password: string): Promise<{ reset: boolean }> {
    return post('/auth/confirm-forgot-password', { email, code, password });
  },
};

/** Decode a JWT payload without verifying the signature (display claims only). */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    const bytes = Uint8Array.from(json, (ch) => ch.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Build the web session shape from a backend token response. */
export function sessionFromTokens(
  tokens: AuthTokens,
  provider: AuthProviderKind,
  email?: string,
): Session {
  const claims = tokens.id_token ? decodeJwtPayload(tokens.id_token) : null;
  return {
    accessToken: tokens.access_token,
    idToken: tokens.id_token,
    refreshToken: tokens.refresh_token,
    expiresAt: Date.now() + (tokens.expires_in ?? 3600) * 1000,
    email: email ?? (typeof claims?.email === 'string' ? claims.email : undefined),
    name: typeof claims?.name === 'string' ? claims.name : undefined,
    sub: typeof claims?.sub === 'string' ? claims.sub : undefined,
    provider,
  };
}
